import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApiService } from '@services/api.service';
import { MatDialogCustomComponent } from '../mat-dialog-custom/mat-dialog-custom.component';
import { Router } from '@angular/router';
import { CorporateTrackingStatus } from '@shared/corporate-tracking-status/corporate-tracking-status.interface';

type ApiResponse<T> = {
  success: boolean;
  records: T;
  record: T;
};

type PaymentRequest = {
  paymentRequestId: string;
  order?: {
    providerName: string;
    orderId: string;
  };
  payment?: {
    amount: number;
    currency: string;
  };
  student?: {
    firstname: string;
    lastname: string;
    country?: string;
    externalId: string;
  };
  paymentLinkUrl: string;
  platform: string;
};

type CorporatePayment = {
  invoiceNumber: string;
  providerName: string;
  amountToSend: number;
  paymentStatus: CorporateTrackingStatus;
  externalTransactionId: string;
  currencyToSend: string;
  id: number;
  companyCurrencyAccount: {
    company_currency_account_id: string;
    external_id: string;
    account_name: string;
    created_at: string;
    updated_at: string;
    currency: {
      currency_id: string;
    };
  };
  invoiceFile?: string;
  proofOfPayment?: string;
};

@Component({
  selector: 'ew-delete-payments',
  templateUrl: './delete-payments.component.html',
  styleUrl: './delete-payments.component.scss',
})
export class DeletePaymentsComponent implements OnInit {
  confirmation = new FormControl(false, [Validators.required]);
  paymentId!: string;
  type: 'PAYMENT_REQUEST' | 'CORPORATE_PAYMENT' | 'COMMISSION' = 'PAYMENT_REQUEST';
  payment!: CorporatePayment | PaymentRequest;
  unlockSendButton: boolean = true;
  ENPOINTS!: string;

  private readonly typeConfig = {
    PAYMENT_REQUEST: {
      endpoint: 'v1/payment-requests/',
      successMessage: 'paymentsDeletion:success',
      errorMessage: 'paymentsDeletion:error',
      redirectPath: '/student-payments'
    },
    CORPORATE_PAYMENT: {
      endpoint: 'v1/invoices/',
      successMessage: 'invoiceFundsDelete:success',
      errorMessage: 'invoiceFundsDelete:error',
      redirectPath: '/invoice-funds'
    },
    COMMISSION: {
      endpoint: 'v1/commissions/',
      successMessage: 'commissionsDelete:success',
      errorMessage: 'commissionsDelete:error',
      redirectPath: '/commission-request'
    }
  };

  constructor(
    private readonly API: ApiService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      paymentId: string;
      type: 'PAYMENT_REQUEST' | 'CORPORATE_PAYMENT' | 'COMMISSION' | null;
    },
    private readonly DIALOG: MatDialogRef<MatDialogCustomComponent>,
    private readonly ROUTER: Router,
  ) {
    this.paymentId = data.paymentId;
    if (data.type) this.type = data.type;
  }

  ngOnInit(): void {
    this.ENPOINTS = this.typeConfig[this.type].endpoint;

    this.API.get<ApiResponse<PaymentRequest>>(
      `${this.ENPOINTS}/${this.paymentId}`,
    ).subscribe((data) => {
      this.payment =
        this.type === 'PAYMENT_REQUEST' ? data.record : data.records;
    });
  }

  get validations() {
    return this.confirmation.value
      ? [this.confirmation.value && this.unlockSendButton]
      : [false];
  }

  deletePayment(paymentId: string) {
    this.unlockSendButton = false;

    const config = this.typeConfig[this.type];
    const successMessageKey = config.successMessage;
    const errorMessageKey = config.errorMessage;
    const redirectPath = config.redirectPath;

    this.API.delete(this.ENPOINTS + paymentId).subscribe(
      () => {
        this.API.alertService.success(successMessageKey);

        setTimeout(() => {
          this.DIALOG.close();
          this.DIALOG.afterClosed().subscribe(() => {
            this.ROUTER.navigateByUrl(redirectPath);
          });
        }, 3_000);
      },
      ({ error: { message } }) => {
        this.API.alertService.error(`${errorMessageKey} - ${message}`);
        this.unlockSendButton = true;
      },
    );
  }

  isPayment(data: CorporatePayment | PaymentRequest): data is PaymentRequest {
    if (!data) {
      return false;
    }

    const props = Reflect.getOwnPropertyDescriptor(data, 'paymentRequestId');

    if (props?.value) {
      return true;
    }
    return false;
  }

  isCommission(_data: any): boolean {
    return this.type === 'COMMISSION';
  }

  isCorporatePayment(_data: any): boolean {
    return this.type === 'CORPORATE_PAYMENT';
  }

  getDisplayId(): string {
    if (this.isPayment(this.payment)) {
      return '#' + this.payment.paymentRequestId;
    }
    return '#' + this.payment.id;
  }

  getAmountDisplay(): string {
    if (this.isPayment(this.payment)) {
      return (this.payment.payment?.amount || 0).toLocaleString('en-US', {
        style: 'currency',
        currency: this.payment.payment?.currency || 'USD'
      });
    }
    
    const amount = (this.payment.amountToSend || 0).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
    
    if (this.isCommission(this.payment)) {
      const currency = (this.payment as any).currencyToSend || 'USD';
      return amount.replace('$', currency + ' ');
    }
    
    if (this.isCorporatePayment(this.payment)) {
      const currency = this.payment.companyCurrencyAccount?.currency?.currency_id || 'USD';
      return amount.replace('$', currency + ' ');
    }
    
    return amount;
  }

  getExternalId(): string {
    if (this.isPayment(this.payment)) {
      return this.payment.student?.externalId || '-';
    }
    
    if (this.isCommission(this.payment)) {
      return (this.payment as any).uuid || this.payment.id || '-';
    }
    
    if (this.isCorporatePayment(this.payment)) {
      return this.payment.companyCurrencyAccount?.external_id || '-';
    }
    
    return '-';
  }

  getInvoiceNumber(): string {
    return (this.payment as any).invoiceNumber || '-';
  }

  getProviderName(): string {
    return (this.payment as any).providerName || '-';
  }
}
